/* ==========================================================================
   Ormonde Jayne — Privacy & Cookies
   Loaded by sections/oj-privacy.liquid.

   The policy itself needs no JavaScript: it is a stack of <details>, so every
   section opens and closes and the copy is readable whatever happens here.

   This file is only the cookie panel. It is a native <dialog>, so Escape,
   focus trapping and inertness are the browser's, and the choices it takes go
   through Shopify's Customer Privacy API rather than into a store of our own
   — the same consent the checkout and the pixels read, so switching a cookie
   off on this page actually switches it off.

   Mapping, policy wording -> Shopify consent category:
     Website functionality  -> none. Always on; the site cannot work without it.
     Website analytics      -> analytics
     Customer preference    -> preferences
     Targeting / advertising-> marketing, and sale_of_data follows it
   ========================================================================== */
(function () {
  'use strict';

  var FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function $all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function privacyApi() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  /* Ask Shopify for the consent API, then hand it back. The callback is given
     the API or null, and is only ever called once. */
  function withPrivacy(done) {
    var api = privacyApi();
    if (api) return done(api);

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(FEATURE, function (error) {
        done(error ? null : privacyApi() || null);
      });
      return;
    }

    done(null);
  }

  /* What the visitor has agreed to right now. Shopify answers '' when it has
     not been asked yet, which we read as "not yet agreed". */
  function currentConsent(api) {
    var consent = {};
    try {
      consent = api.currentVisitorConsent() || {};
    } catch (e) {
      consent = {};
    }
    return {
      analytics: consent.analytics === 'yes',
      preferences: consent.preferences === 'yes',
      marketing: consent.marketing === 'yes'
    };
  }

  function init(root) {
    if (root.__ojPrivacyInit) return;
    root.__ojPrivacyInit = true;

    var pop = root.querySelector('[data-oj-pv-pop]');
    if (!pop || typeof pop.showModal !== 'function') return;

    var live = root.querySelector('[data-live]');
    var note = pop.querySelector('[data-note]');
    var acceptBtn = pop.querySelector('[data-act="accept"]');
    var saveBtn = pop.querySelector('[data-act="save"]');
    var essentialBtn = pop.querySelector('[data-act="essential"]');
    var triggers = $all('[data-oj-pv-open]', root);

    var api = null;
    var unavailable = pop.getAttribute('data-unavailable-note') || '';
    var savedNote = pop.getAttribute('data-saved-note') || '';
    var defaultNote = note ? note.textContent : '';

    function say(message) {
      if (live) live.textContent = message;
    }

    function boxFor(id) {
      return pop.querySelector('input[data-cookie="' + id + '"]');
    }

    function setLabel(input) {
      var label = input.parentNode.querySelector('[data-check-label]');
      if (label) label.textContent = input.checked ? input.getAttribute('data-on') : input.getAttribute('data-off');
    }

    function fill(state) {
      Object.keys(state).forEach(function (key) {
        var input = boxFor(key);
        if (!input) return;
        input.checked = !!state[key];
        setLabel(input);
      });
    }

    function readBoxes() {
      var state = { analytics: false, preferences: false, marketing: false };
      Object.keys(state).forEach(function (key) {
        var input = boxFor(key);
        if (input) state[key] = input.checked;
      });
      return state;
    }

    function close() {
      if (pop.open) pop.close();
    }

    /* Write the choice through Shopify and shut the panel. If the API never
       arrived we say so rather than pretending the choice landed. */
    function store(state) {
      if (!api) {
        if (note && unavailable) note.textContent = unavailable;
        say(unavailable);
        return;
      }

      try {
        api.setTrackingConsent(
          {
            analytics: !!state.analytics,
            preferences: !!state.preferences,
            marketing: !!state.marketing,
            sale_of_data: !!state.marketing
          },
          function (result) {
            if (result && result.error) {
              if (note && unavailable) note.textContent = unavailable;
              say(unavailable);
              return;
            }
            say(savedNote);
            close();
          }
        );
      } catch (e) {
        if (note && unavailable) note.textContent = unavailable;
        say(unavailable);
      }
    }

    /* Left to itself the browser focuses the first checkbox, which scrolls the
       sheet past its own heading — on a phone the reader never sees what they
       have been asked. Put focus on the heading instead, at the top. */
    function open() {
      if (note) note.textContent = defaultNote;
      if (api) fill(currentConsent(api));
      pop.showModal();
      pop.scrollTop = 0;
      var title = pop.querySelector('h2');
      if (title) title.focus({ preventScroll: true });
    }

    $all('input[data-cookie]', pop).forEach(function (input) {
      input.addEventListener('change', function () {
        setLabel(input);
      });
    });

    triggers.forEach(function (trigger) {
      trigger.hidden = false;
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        open();
      });
    });

    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        store({ analytics: true, preferences: true, marketing: true });
      });
    }

    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        store(readBoxes());
      });
    }

    if (essentialBtn) {
      essentialBtn.addEventListener('click', function () {
        store({ analytics: false, preferences: false, marketing: false });
      });
    }

    /* Clicking the backdrop closes the panel. A click that lands on the
       <dialog> itself but outside its padded inner box is the backdrop. */
    pop.addEventListener('click', function (event) {
      if (event.target === pop) close();
    });

    withPrivacy(function (found) {
      api = found;
      if (!api && note && unavailable) note.textContent = unavailable;
      if (api && pop.open) fill(currentConsent(api));
    });

    /* Another surface — Shopify's own banner, say — may change consent while
       this page is open. Keep the panel honest about it. */
    document.addEventListener('visitorConsentCollected', function () {
      if (api && pop.open) fill(currentConsent(api));
    });

    /* /pages/privacy-cookies#cookie-preferences opens the panel on arrival,
       so the footer and the banner have somewhere to point. */
    if (window.location.hash === '#cookie-preferences') {
      withPrivacy(function (found) {
        api = found;
        open();
      });
    }
  }

  ready(function () {
    $all('[data-oj-pv]').forEach(init);
  });
})();
